import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthShell from './AuthShell';
import Toast from '../../components/forms/Toast';
import Icon from '../../components/common/Icon';
import WhatsAppNotice from '../../components/common/WhatsAppNotice';
import { apiRequest, handleBranding } from '../../utils';

/* Public accept-invite page (/join/:token). Two kinds of invite land here:
   - a consumer TEAM invite: accepting provisions the account and signs the
     colleague straight into their team (auto-join).
   - a company/community invite (requires_approval): submitting files a pending
     request the contact person must approve — we show a "request sent" state and
     do NOT sign the person in. The shareable link carries no email, so we ask for it.
   Backend: GET /api/team-invite/:token + POST /api/accept-team-invite. */
const AcceptInvite = () => {
  const { t } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [requestSent, setRequestSent] = useState(false);

  useEffect(() => {
    handleBranding();
    (async () => {
      try {
        const data = await apiRequest('GET', `/team-invite/${token}`);
        setInfo(data);
      } catch (err) {
        setError(t(err.message, { defaultValue: err.message }));
        setShowToast(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [token, t]);

  const isCommunity = info && info.is_community;

  // After a request is filed, don't dead-end on "request sent" — offer a click
  // through to the event's commercial website (external), or the on-platform
  // public event page as a fallback.
  const ctaHref = info && (info.commercial_url || (info.event_id ? `/public-event/${info.event_id}` : null));
  const ctaExternal = Boolean(info && info.commercial_url);

  const accept = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const data = await apiRequest('POST', '/accept-team-invite', {
        token,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        mobile: mobile.trim(),
        ...(info && info.needs_email ? { email: email.trim() } : {}),
      });
      if (data && data.pending) {
        // Company/community join: waiting for the contact person's approval.
        setRequestSent(true);
        return;
      }
      localStorage.setItem('isLoggedIn', '1');
      window.dispatchEvent(new Event('auth-changed'));
      navigate(data.redirect || '/team');
    } catch (err) {
      setError(t(err.message, { defaultValue: err.message }));
      setShowToast(true);
    } finally {
      setSubmitting(false);
    }
  };

  const valid = info && info.valid;
  const targetName = info && (info.company_name || info.team_name);
  const title = valid
    ? (info.requires_approval
        ? t('requestToJoinTitle', { name: targetName, defaultValue: `Join ${targetName}` })
        : t('joinTeamTitle', { team: info.team_name }))
    : t('teamInvite', { defaultValue: 'Team invite' });

  return (
    <AuthShell
      title={requestSent ? t('requestSentTitle', { defaultValue: 'Request sent' }) : title}
      subtitle={valid && !requestSent ? (info.company_name ? '' : (info.event_name || '')) : ''}
    >
      {loading ? (
        <div className="auth__card"><span className="auth__spinner" /></div>
      ) : requestSent ? (
        <div className="auth__card">
          <p>{isCommunity
            ? t('requestSentMemberBody', { name: targetName, defaultValue: 'Your request was sent. The community contact will approve it shortly.' })
            : t('requestSentBody', { name: targetName, defaultValue: 'Your request was sent. The contact person will approve it shortly.' })}</p>
          {ctaHref && (
            <a
              className="auth__primary"
              href={ctaHref}
              {...(ctaExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            >
              <Icon name="globe" />
              {t('learnMoreAboutEvent', { event: info.event_name || '' })}
            </a>
          )}
        </div>
      ) : !valid ? (
        <div className="auth__card">
          <p>{t('invalidInvite')}</p>
        </div>
      ) : (
        <form className="auth__card" onSubmit={accept}>
          <div className="auth__row">
            <div className="auth__field">
              <label className="auth__label">{t('firstName')}</label>
              <div className="auth__input-wrap">
                <input
                  className="auth__input"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder={t('enterFirstName')}
                  autoComplete="given-name"
                  required
                />
              </div>
            </div>
            <div className="auth__field">
              <label className="auth__label">{t('lastName')}</label>
              <div className="auth__input-wrap">
                <input
                  className="auth__input"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder={t('enterLastName')}
                  autoComplete="family-name"
                  required
                />
              </div>
            </div>
          </div>

          {info.needs_email && (
            <div className="auth__field">
              <label className="auth__label">{t('email', { defaultValue: 'Email' })}</label>
              <div className="auth__input-wrap">
                <input
                  className="auth__input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('enterEmail', { defaultValue: 'you@example.com' })}
                  autoComplete="email"
                  required
                />
              </div>
            </div>
          )}

          <div className="auth__field">
            <label className="auth__label">{t('mobileNumber', { defaultValue: 'Mobile number' })}</label>
            <div className="auth__input-wrap">
              <input
                className="auth__input"
                type="tel"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                placeholder={t('enterMobile', { defaultValue: '+32 470 12 34 56' })}
                autoComplete="tel"
                required
              />
            </div>
            <WhatsAppNotice />
          </div>

          <button type="submit" className="auth__primary" disabled={submitting}>
            {submitting ? <span className="auth__spinner" /> : <Icon name="user-check" />}
            {info.requires_approval
              ? t('sendJoinRequest', { defaultValue: 'Request to join' })
              : t('joinTheTeam')}
          </button>

          <Toast showToast={showToast} setShowToast={setShowToast} message={error} />
        </form>
      )}
    </AuthShell>
  );
};

export default AcceptInvite;
